<template>
  <v-container v-if="getProfile.role === 1" fluid class="pa-0 mb-6">
    <v-card v-if="storageError" class="storage-card" elevation="3">
      <v-card-text class="pa-6 text-center">
        <v-icon size="48" color="error" class="mb-3">mdi-alert-circle-outline</v-icon>
        <h3 class="text-h6 error--text mb-2">{{ $t('error_cargar_almacenamiento') || 'Error al cargar almacenamiento' }}</h3>
        <p class="text-body2 grey--text">{{ $t('verifica_permisos') || 'Verifica tus permisos o intenta más tarde' }}</p>
        <v-btn small color="primary" outlined @click="loadStorageData" class="mt-3">
          <v-icon small left>mdi-refresh</v-icon>
          {{ $t('reintentar') || 'Reintentar' }}
        </v-btn>
      </v-card-text>
    </v-card>

    <v-card v-else-if="loadingStorage" class="storage-card" elevation="3">
      <v-card-text class="pa-6 text-center">
        <v-progress-circular indeterminate size="48" color="primary" class="mb-4"></v-progress-circular>
        <p class="text-body2 grey--text">{{ $t('cargando_almacenamiento') || 'Cargando información de almacenamiento...' }}</p>
      </v-card-text>
    </v-card>

    <v-card v-else class="storage-card" elevation="3">
      <!-- Header -->
      <div class="storage-header">
        <div class="header-left">
          <div class="header-icon" :style="{ background: statusColor + '1A', color: statusColor }">
            <v-icon :color="statusColor">{{ storageIcon }}</v-icon>
          </div>
          <div>
            <div class="header-title">{{ $t('almacenamiento') }}</div>
            <div class="header-subtitle">{{ residentialName }}</div>
          </div>
        </div>
        <div class="header-right">
          <div class="status-badge" :style="{ background: statusColor + '14', color: statusColor }">
            <v-icon :size="14" :color="statusColor">{{ statusIcon }}</v-icon>
            <span>{{ statusLabel }}</span>
          </div>
          <div class="updated-label">
            <v-icon :size="13" class="mr-1">mdi-clock-outline</v-icon>{{ lastUpdated }}
          </div>
        </div>
      </div>

      <v-divider></v-divider>

      <!-- Body -->
      <div class="storage-body">
        <!-- Gauge -->
        <div class="gauge-section">
          <div class="gauge-wrap">
            <svg viewBox="0 0 120 120" class="gauge-svg">
              <circle
                class="gauge-track"
                cx="60" cy="60" r="52"
                :stroke="trackColor"
              />
              <circle
                class="gauge-fill"
                cx="60" cy="60" r="52"
                :stroke="statusColor"
                :stroke-dasharray="gaugeDashArray"
                stroke-linecap="round"
              />
            </svg>
            <div class="gauge-center">
              <span class="gauge-value">{{ usedPercentage }}<small>%</small></span>
              <span class="gauge-caption">{{ $t('usado') }}</span>
            </div>
          </div>
        </div>

        <!-- Divider vertical -->
        <div class="body-divider"></div>

        <!-- Stats grid -->
        <div class="stats-grid">
          <div class="stat-item">
            <div class="stat-icon-sm" style="color:#2a78d6">
              <v-icon :size="18" color="#2a78d6">mdi-harddisk</v-icon>
            </div>
            <div>
              <div class="stat-value">{{ usedStorageThisMonth.toFixed(2) }} GB</div>
              <div class="stat-label">{{ $t('este_mes') }}</div>
            </div>
          </div>
          <div class="stat-item">
            <div class="stat-icon-sm">
              <v-icon :size="18" color="#0ca30c">mdi-database-check-outline</v-icon>
            </div>
            <div>
              <div class="stat-value">{{ formatBytes(availableStorage) }}</div>
              <div class="stat-label">{{ $t('disponible') }}</div>
            </div>
          </div>
          <div class="stat-item">
            <div class="stat-icon-sm">
              <v-icon :size="18" color="#52514e">mdi-server</v-icon>
            </div>
            <div>
              <div class="stat-value">{{ formatBytes(totalStorage) }}</div>
              <div class="stat-label">{{ $t('limite') }}</div>
            </div>
          </div>
          <div class="stat-item">
            <div class="stat-icon-sm">
              <v-icon :size="18" :color="growthColor">{{ growthIcon }}</v-icon>
            </div>
            <div>
              <div class="stat-value">{{ growthFormatted }}</div>
              <div class="stat-label">{{ $t('este_mes') }}</div>
            </div>
          </div>
        </div>
      </div>

      <v-divider></v-divider>

      <!-- Storage Breakdown -->
      <div class="storage-details-section">
        <div class="detail-item">
          <span class="detail-label">{{ $t('almacenamiento_historico') }}</span>
          <span class="detail-value">{{ historicStorageGB }} GB</span>
        </div>
        <div class="detail-item">
          <span class="detail-label">{{ $t('almacenamiento_reciente') }}</span>
          <span class="detail-value">{{ recentStorageGB }} GB</span>
        </div>
        <div class="detail-item">
          <span class="detail-label">{{ $t('total_historico') }}</span>
          <span class="detail-value">{{ totalStorageGB }} GB</span>
        </div>
      </div>

      <v-divider></v-divider>

      <!-- Footer: breakdown -->
      <div class="storage-footer">
        <div class="footer-item">
          <v-icon :size="16" color="#4a3aa7" class="mr-2">mdi-image-multiple-outline</v-icon>
          <span class="footer-value">{{ photosCount.toLocaleString() }}</span>
          <span class="footer-label">{{ $t('fotos') }}</span>
        </div>
        <div class="footer-sep"></div>
        <div class="footer-item">
          <v-icon :size="16" color="#eb6834" class="mr-2">mdi-file-document-outline</v-icon>
          <span class="footer-value">{{ documentsCount.toLocaleString() }}</span>
          <span class="footer-label">{{ $t('documentos') }}</span>
        </div>
        <div class="footer-sep"></div>
        <div class="footer-item">
          <v-icon :size="16" color="#1baf7a" class="mr-2">mdi-motion-sensor</v-icon>
          <span class="footer-value">{{ eventsCount.toLocaleString() }}</span>
          <span class="footer-label">{{ $t('eventos') }}</span>
        </div>
        <v-spacer></v-spacer>
        <v-btn
          small
          outlined
          color="primary"
          @click="showPackagesDialog = true"
          class="ml-4"
        >
          <v-icon small left>mdi-plus</v-icon>
          {{ $t('solicitar_espacio') }}
        </v-btn>
      </div>
    </v-card>

    <!-- Dialog: Storage Packages -->
    <v-dialog v-model="showPackagesDialog" max-width="980px" scrollable>
      <v-card class="packages-dialog">
        <!-- Header -->
        <div class="packages-header">
          <div class="packages-header-icon">
            <v-icon size="24" color="white">mdi-cloud-upload-outline</v-icon>
          </div>
          <div class="packages-header-text">
            <div class="packages-title">{{ $t('solicitar_almacenamiento') }}</div>
            <div class="packages-subtitle">{{ $t('solicitar_almacenamiento_subtitulo') }}</div>
          </div>
          <v-btn icon class="packages-close" @click="closePackagesDialog">
            <v-icon color="white">mdi-close</v-icon>
          </v-btn>
        </div>

        <v-card-text class="packages-body">
          <!-- Current usage banner -->
          <div class="current-usage-banner">
            <v-icon :color="statusColor" size="20" class="mr-2">mdi-information-outline</v-icon>
            <span>
              {{ $t('uso_actual') }}:
              <strong>{{ usedFormatted }}</strong> {{ $t('prep_de') }} <strong>{{ totalFormatted }}</strong>
              ({{ usedPercentage }}%)
            </span>
          </div>

          <!-- Storage Selection -->
          <div class="storage-selection-section">
            <h3 class="selection-title">{{ $t('almacenamiento_adicional') || 'Almacenamiento Adicional' }}</h3>
          </div>

          <!-- Package grid -->
          <v-row class="package-grid" no-gutters>
            <v-col
              v-for="pkg in storagePackages"
              :key="pkg.id"
              cols="12" sm="6" md="3"
              class="package-col"
            >
              <div
                class="package-card"
                :class="{ 'package-card--selected': selectedPackage === pkg.id, 'package-card--popular': pkg.popular }"
                @click="selectedPackage = pkg.id"
              >
                <div v-if="pkg.popular" class="package-badge">
                  <v-icon size="12" class="mr-1">mdi-star</v-icon>{{ $t('mas_popular') }}
                </div>

                <div class="package-check" v-if="selectedPackage === pkg.id">
                  <v-icon size="14" color="white">mdi-check</v-icon>
                </div>

                <div class="package-icon-wrap" :style="{ background: pkg.color + '1A' }">
                  <v-icon size="26" :color="pkg.color">{{ pkg.icon }}</v-icon>
                </div>

                <div class="package-storage-amount">{{ pkg.name }}</div>
                <div class="package-description">{{ $t(pkg.descriptionKey) }}</div>

                <div class="package-price">
                  <span class="price-value">{{ pkg.price }}</span>
                  <span class="price-period">{{ $t('por_mes') }}</span>
                </div>

                <v-divider class="package-divider"></v-divider>

                <ul class="package-features">
                  <li v-for="featureKey in pkg.featureKeys" :key="featureKey">
                    <v-icon size="14" color="#0ca30c" class="mr-1">mdi-check-circle</v-icon>
                    {{ $t(featureKey) }}
                  </li>
                </ul>
              </div>
            </v-col>
          </v-row>

          <!-- Duration Selection -->
          <div class="duration-selection-section">
            <h3 class="selection-title">{{ $t('duracion_plan') || 'Duración del Plan' }}</h3>
            <v-row class="duration-grid" no-gutters>
              <v-col
                v-for="duration in durationOptions"
                :key="duration.id"
                cols="6" sm="3"
                class="duration-col"
              >
                <div
                  class="duration-card"
                  :class="{ 'duration-card--selected': selectedDuration === duration.months }"
                  @click="selectedDuration = duration.months"
                >
                  <div class="duration-label">{{ duration.label }}</div>
                  <div v-if="duration.discount > 0" class="duration-discount">-{{ duration.discount }}%</div>
                </div>
              </v-col>
            </v-row>
          </div>

          <!-- Price Breakdown -->
          <div v-if="selectedPackageData" class="price-breakdown">
            <div class="breakdown-item">
              <span class="breakdown-label">{{ $t('precio_por_mes') || 'Precio por mes' }}</span>
              <span class="breakdown-value">${{ monthlyPrice.toFixed(2) }} MXN</span>
            </div>
            <div class="breakdown-item">
              <span class="breakdown-label">{{ $t('meses') || 'Meses' }}</span>
              <span class="breakdown-value">{{ selectedDuration }}</span>
            </div>
            <div class="breakdown-item">
              <span class="breakdown-label">{{ $t('subtotal') || 'Subtotal' }}</span>
              <span class="breakdown-value">${{ subtotal.toFixed(2) }} MXN</span>
            </div>
            <v-divider class="breakdown-divider"></v-divider>
            <div v-if="discountAmount > 0" class="breakdown-item breakdown-discount">
              <span class="breakdown-label">{{ $t('descuento') || 'Descuento' }} (-{{ discountPercent }}%)</span>
              <span class="breakdown-value">-${{ discountAmount.toFixed(2) }} MXN</span>
            </div>
            <div class="breakdown-item breakdown-total">
              <span class="breakdown-label">{{ $t('total') || 'Total' }}</span>
              <span class="breakdown-value">{{ formatCurrency(totalPrice) }}</span>
            </div>
          </div>
        </v-card-text>

        <v-divider></v-divider>

        <v-card-actions class="packages-actions">
          <div v-if="selectedPackageData && selectedDurationData" class="selection-summary">
            <v-icon size="18" color="primary" class="mr-1">mdi-package-variant-closed</v-icon>
            <div style="flex: 1;">
              <div class="selection-summary-label">{{ $t('paquete_seleccionado') }}:</div>
              <div class="selection-summary-value">{{ selectedPackageData.name }} × {{ selectedDurationData.label }}</div>
              <div class="selection-summary-price">Total: {{ formatCurrency(totalPrice) }}</div>
            </div>
          </div>
          <div v-else class="selection-summary selection-summary--empty">
            {{ $t('ningun_paquete_seleccionado') }}
          </div>
          <v-spacer></v-spacer>
          <v-btn color="grey darken-1" text @click="closePackagesDialog">
            {{ $t('cancelar') }}
          </v-btn>
          <v-btn
            color="primary"
            rounded
            elevation="0"
            :disabled="selectedPackage === null"
            :loading="loadingRequest"
            @click="requestStorageUpgrade"
          >
            <v-icon left small>mdi-send</v-icon>
            {{ $t('solicitar') }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script>
import { mapGetters } from 'vuex'
import storageService from '@/services/storageService'

export default {
  name: 'StorageCard',
  data () {
    return {
      usedStorageThisMonth: 0,
      historicStorageGB: 0,
      recentStorageGB: 0,
      totalStorageGB: 0,
      totalStorage: 100,
      availableStorage: 100,
      usagePercentage: 0,
      monthlyGrowthGB: 0,
      photosCount: 0,
      documentsCount: 0,
      eventsCount: 0,
      residentialName: '',
      lastUpdated: '--',
      showPackagesDialog: false,
      selectedPackage: null,
      loadingRequest: false,
      loadingStorage: true,
      storageError: false,
      requestError: null,
      storagePackages: [
        { id: 1, storage: 5, name: '+5 GB', icon: 'mdi-database-outline', color: '#43A047', popular: false, descriptionKey: 'paquete_descripcion_5gb', featureKeys: ['activacion_inmediata', 'sin_permanencia'] },
        { id: 2, storage: 10, name: '+10 GB', icon: 'mdi-database', color: '#3F51B5', popular: true, descriptionKey: 'paquete_descripcion_10gb', featureKeys: ['activacion_inmediata', 'sin_permanencia', 'facturacion_mensual'] },
        { id: 3, storage: 20, name: '+20 GB', icon: 'mdi-database-plus', color: '#2E7D32', popular: false, descriptionKey: 'paquete_descripcion_20gb', featureKeys: ['activacion_inmediata', 'sin_permanencia', 'soporte_prioritario'] },
        { id: 4, storage: 50, name: '+50 GB', icon: 'mdi-database-lock', color: '#283593', popular: false, descriptionKey: 'paquete_descripcion_50gb', featureKeys: ['activacion_inmediata', 'facturacion_mensual', 'soporte_prioritario'] },
        { id: 5, storage: 100, name: '+100 GB', icon: 'mdi-database-arrow-down', color: '#1565c0', popular: false, descriptionKey: 'paquete_descripcion_100gb', featureKeys: ['activacion_inmediata', 'facturacion_mensual', 'soporte_prioritario'] },
        { id: 6, storage: 200, name: '+200 GB', icon: 'mdi-database-arrow-down-outline', color: '#0d47a1', popular: false, descriptionKey: 'paquete_descripcion_200gb', featureKeys: ['activacion_inmediata', 'facturacion_mensual', 'soporte_prioritario'] }
      ],
      durationOptions: [
        { id: 1, months: 1, label: '1 Mes', discount: 0 },
        { id: 2, months: 3, label: '3 Meses', discount: 5 },
        { id: 3, months: 6, label: '6 Meses', discount: 10 },
        { id: 4, months: 12, label: '12 Meses', discount: 15 }
      ],
      selectedDuration: 1
    }
  },
  computed: {
    ...mapGetters(['getProfile']),
    usedPercentage () {
      return this.usagePercentage || 0
    },
    usedFormatted () {
      return this.formatBytes(this.usedStorage)
    },
    availableFormatted () {
      return this.formatBytes(this.availableStorage)
    },
    totalFormatted () {
      return this.formatBytes(this.totalStorage)
    },
    growthFormatted () {
      const sign = this.monthlyGrowthGB >= 0 ? '+' : ''
      return sign + this.formatBytes(this.monthlyGrowthGB)
    },
    growthIcon () {
      return this.monthlyGrowthGB >= 0 ? 'mdi-trending-up' : 'mdi-trending-down'
    },
    growthColor () {
      return '#898781'
    },
    // Status: good < 70%, warning 70-90%, critical > 90% (fixed status scale, never themed)
    statusColor () {
      const p = this.usedPercentage
      if (p < 70) return '#0ca30c'
      if (p < 90) return '#c98500'
      return '#d03b3b'
    },
    trackColor () {
      const p = this.usedPercentage
      if (p < 70) return '#cdeccd'
      if (p < 90) return '#f7e0b0'
      return '#f5cccb'
    },
    statusIcon () {
      const p = this.usedPercentage
      if (p < 70) return 'mdi-check-circle'
      if (p < 90) return 'mdi-alert'
      return 'mdi-alert-octagon'
    },
    storageIcon () {
      const p = this.usedPercentage
      if (p < 50) return 'mdi-database-outline'
      if (p < 80) return 'mdi-database'
      return 'mdi-database-alert-outline'
    },
    statusLabel () {
      const p = this.usedPercentage
      if (p < 70) return this.$t('optimo')
      if (p < 90) return this.$t('alerta')
      return this.$t('critico')
    },
    gaugeDashArray () {
      const circumference = 2 * Math.PI * 52
      const filled = (this.usedPercentage / 100) * circumference
      return `${filled} ${circumference}`
    },
    selectedPackageData () {
      return this.storagePackages.find(p => p.id === this.selectedPackage) || null
    },
    selectedDurationData () {
      return this.durationOptions.find(d => d.months === this.selectedDuration) || null
    },
    pricePerGB () {
      return 18
    },
    monthlyPrice () {
      if (!this.selectedPackageData) return 0
      return this.selectedPackageData.storage * this.pricePerGB
    },
    discountPercent () {
      if (!this.selectedDurationData) return 0
      return this.selectedDurationData.discount
    },
    subtotal () {
      return this.monthlyPrice * this.selectedDuration
    },
    discountAmount () {
      return this.subtotal * (this.discountPercent / 100)
    },
    totalPrice () {
      return this.subtotal - this.discountAmount
    }
  },
  created () {
    this.loadStorageData()
    this.residentialName = this.getProfile.housing_unit_name || 'Fraccionamiento'
  },
  methods: {
    async loadStorageData () {
      this.loadingStorage = true
      this.storageError = false

      try {
        const today = new Date()
        const currentMonthStart = new Date(today.getFullYear(), today.getMonth(), 1)
        const lastMonthStart = new Date(today.getFullYear(), today.getMonth() - 1, 1)
        const lastMonthEnd = new Date(today.getFullYear(), today.getMonth(), 0)
        const historicStart = new Date(2020, 0, 1)

        const [currentMonthStats, lastMonthStats, totalHistoricStats] = await Promise.all([
          storageService.getStorageStats(currentMonthStart, today),
          storageService.getStorageStats(lastMonthStart, lastMonthEnd),
          storageService.getStorageStats(historicStart, today)
        ])

        const currentMonthRecentGB = (currentMonthStats.recent_storage_bytes / (1024 * 1024 * 1024)).toFixed(2)
        const lastMonthRecentGB = (lastMonthStats.recent_storage_bytes / (1024 * 1024 * 1024)).toFixed(2)

        this.usedStorageThisMonth = parseFloat(currentMonthRecentGB)
        this.historicStorageGB = (totalHistoricStats.historic_storage_bytes / (1024 * 1024 * 1024)).toFixed(2)
        this.recentStorageGB = (totalHistoricStats.recent_storage_bytes / (1024 * 1024 * 1024)).toFixed(2)
        this.totalStorageGB = (totalHistoricStats.total_size_bytes / (1024 * 1024 * 1024)).toFixed(2)

        this.totalStorage = currentMonthStats.storage_limit_gb || 100
        this.availableStorage = currentMonthStats.available_gb || (this.totalStorage - parseFloat(this.totalStorageGB))
        this.usagePercentage = currentMonthStats.usage_percentage || Math.round((parseFloat(this.totalStorageGB) / this.totalStorage) * 100)

        this.photosCount = Math.round(currentMonthStats.total_files * 0.4)
        this.documentsCount = Math.round(currentMonthStats.total_files * 0.3)
        this.eventsCount = Math.round(currentMonthStats.total_files * 0.3)

        this.monthlyGrowthGB = parseFloat((parseFloat(currentMonthRecentGB) - parseFloat(lastMonthRecentGB)).toFixed(2))

        this.lastUpdated = this.$t('hace_un_momento') || 'Hace un momento'
      } catch (error) {
        console.error('Error loading storage data:', error)
        this.storageError = true
        if (error.response && error.response.status === 403) {
          console.error('No tienes permisos para ver esta información')
        }
      } finally {
        this.loadingStorage = false
      }
    },
    formatLastUpdate (date) {
      const now = new Date()
      const diffMs = now - date
      const diffMins = Math.floor(diffMs / 60000)
      const diffHours = Math.floor(diffMs / 3600000)
      const diffDays = Math.floor(diffMs / 86400000)

      if (diffMins < 1) return this.$t('hace_un_momento') || 'Hace un momento'
      if (diffMins < 60) return `Hace ${diffMins} min`
      if (diffHours < 24) return `Hace ${diffHours}h`
      if (diffDays < 7) return `Hace ${diffDays}d`

      return date.toLocaleDateString()
    },
    formatBytes (value) {
      const abs = Math.abs(value)
      if (abs < 1024) return value.toFixed(1) + ' GB'
      return (value / 1024).toFixed(1) + ' TB'
    },
    formatCurrency (value) {
      return '$' + value.toFixed(2) + ' MXN'
    },
    closePackagesDialog () {
      this.showPackagesDialog = false
      this.selectedPackage = null
    },
    async requestStorageUpgrade () {
      if (!this.selectedPackageData || !this.selectedDurationData) return
      this.loadingRequest = true
      this.requestError = null
      const packageName = this.selectedPackageData.name
      const packageData = this.selectedPackageData

      try {
        const storageBytes = packageData.storage * 1024 * 1024 * 1024
        const requestDescription = `${packageData.name} x ${this.selectedDurationData.label} - Total: $${this.totalPrice.toFixed(2)}`

        await storageService.createStorageRequest(
          packageData.name,
          storageBytes,
          requestDescription,
          this.selectedDuration,
          this.totalPrice
        )

        this.loadingRequest = false
        this.showPackagesDialog = false
        this.selectedPackage = null
        this.selectedDuration = 1
        this.$store.commit('snackbar_activate_true')
        this.$store.commit('snackbar_change_message', this.$t('solicitud_enviada', { paquete: packageName }))
        this.loadStorageData()
      } catch (error) {
        console.error('Error requesting storage upgrade:', error)
        this.loadingRequest = false
        this.requestError = this.$t('error_solicitud_almacenamiento') || 'Error al enviar solicitud'
        this.$store.commit('snackbar_activate_true')
        this.$store.commit('snackbar_change_message', this.requestError)
      }
    }
  }
}
</script>

<style scoped>
.storage-card {
  border-radius: 16px;
  overflow: hidden;
  transition: box-shadow 0.25s ease, transform 0.25s ease;
}

.storage-card:hover {
  transform: translateY(-2px);
}

/* Header */
.storage-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 18px 20px;
  gap: 12px;
  flex-wrap: wrap;
}

.header-left {
  display: flex;
  align-items: center;
  gap: 14px;
}

.header-icon {
  width: 44px;
  height: 44px;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.header-title {
  font-size: 16px;
  font-weight: 600;
  color: #0b0b0b;
  line-height: 1.3;
}

.header-subtitle {
  font-size: 12.5px;
  color: #898781;
  line-height: 1.3;
}

.header-right {
  display: flex;
  align-items: center;
  gap: 12px;
}

.status-badge {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 4px 10px;
  border-radius: 20px;
  font-size: 12px;
  font-weight: 600;
}

.updated-label {
  display: flex;
  align-items: center;
  font-size: 12px;
  color: #898781;
}

/* Body */
.storage-body {
  display: flex;
  align-items: center;
  padding: 24px 20px;
  gap: 24px;
  flex-wrap: wrap;
}

.gauge-section {
  display: flex;
  justify-content: center;
  flex-shrink: 0;
}

.gauge-wrap {
  position: relative;
  width: 132px;
  height: 132px;
}

.gauge-svg {
  width: 100%;
  height: 100%;
  transform: rotate(-90deg);
}

.gauge-track,
.gauge-fill {
  fill: none;
  stroke-width: 10;
}

.gauge-fill {
  transition: stroke-dasharray 0.6s ease, stroke 0.3s ease;
}

.gauge-center {
  position: absolute;
  inset: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
}

.gauge-value {
  font-size: 28px;
  font-weight: 700;
  color: #0b0b0b;
  line-height: 1;
}

.gauge-value small {
  font-size: 15px;
  font-weight: 600;
  margin-left: 1px;
}

.gauge-caption {
  font-size: 11px;
  color: #898781;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin-top: 4px;
}

.body-divider {
  width: 1px;
  align-self: stretch;
  background: #e1e0d9;
  flex-shrink: 0;
}

.stats-grid {
  flex: 1;
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 20px 24px;
  min-width: 220px;
}

.stat-item {
  display: flex;
  align-items: center;
  gap: 10px;
}

.stat-icon-sm {
  width: 34px;
  height: 34px;
  border-radius: 9px;
  background: #f9f9f7;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.stat-value {
  font-size: 16px;
  font-weight: 600;
  color: #0b0b0b;
  font-variant-numeric: tabular-nums;
  line-height: 1.2;
}

.stat-label {
  font-size: 11.5px;
  color: #898781;
  line-height: 1.2;
  margin-top: 1px;
}

/* Footer */
.storage-footer {
  display: flex;
  align-items: center;
  padding: 14px 20px;
  background: #f9f9f7;
  flex-wrap: wrap;
  gap: 8px 0;
  justify-content: space-between;
}

.footer-item {
  display: flex;
  align-items: center;
  padding: 0 16px;
}

.footer-value {
  font-size: 13px;
  font-weight: 600;
  color: #0b0b0b;
  font-variant-numeric: tabular-nums;
  margin-right: 5px;
}

.footer-label {
  font-size: 12px;
  color: #898781;
}

.footer-sep {
  width: 1px;
  height: 14px;
  background: #e1e0d9;
}

/* Storage Details Section */
.storage-details-section {
  display: flex;
  flex-direction: column;
  padding: 16px 20px;
  gap: 12px;
  background: #f9f9f7;
}

.detail-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-size: 13px;
}

.detail-label {
  color: #898781;
  font-weight: 500;
}

.detail-value {
  color: #0b0b0b;
  font-weight: 600;
  font-variant-numeric: tabular-nums;
}

/* Packages Dialog */
.packages-dialog {
  border-radius: 16px;
  overflow: hidden;
}

.packages-header {
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 22px 24px;
  background: var(--v-primary-base, #43A047);
}

.packages-header-icon {
  width: 44px;
  height: 44px;
  border-radius: 12px;
  background: rgba(255, 255, 255, 0.18);
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.packages-header-text {
  flex: 1;
  min-width: 0;
}

.packages-title {
  font-size: 18px;
  font-weight: 700;
  color: #ffffff;
  line-height: 1.3;
}

.packages-subtitle {
  font-size: 13px;
  color: rgba(255, 255, 255, 0.85);
  line-height: 1.3;
  margin-top: 2px;
}

.packages-close {
  flex-shrink: 0;
}

.packages-body {
  padding: 24px !important;
}

.current-usage-banner {
  display: flex;
  align-items: center;
  padding: 12px 16px;
  border-radius: 10px;
  background: #f2f6fc;
  color: #3a3a38;
  font-size: 13.5px;
  margin-bottom: 24px;
}

.package-grid {
  margin: -10px;
  width: calc(100% + 20px);
}

.package-col {
  padding: 10px !important;
}

.package-card {
  position: relative;
  cursor: pointer;
  height: 100%;
  padding: 22px 18px;
  border-radius: 14px;
  border: 2px solid #e1e0d9;
  background: #ffffff;
  text-align: center;
  transition: border-color 0.2s ease, box-shadow 0.2s ease, transform 0.2s ease;
}

.package-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 6px 16px rgba(0, 0, 0, 0.08);
}

.package-card--selected {
  border-color: var(--v-primary-base, #43A047);
  background-color: rgba(67, 160, 71, 0.06);
  box-shadow: 0 6px 16px rgba(67, 160, 71, 0.15);
}

.package-card--popular {
  border-color: rgba(63, 81, 181, 0.35);
}

.package-badge {
  position: absolute;
  top: -11px;
  left: 50%;
  transform: translateX(-50%);
  display: inline-flex;
  align-items: center;
  padding: 3px 10px;
  border-radius: 20px;
  background: var(--v-accent-base, #3F51B5);
  color: #ffffff;
  font-size: 10.5px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.4px;
  white-space: nowrap;
}

.package-check {
  position: absolute;
  top: 12px;
  right: 12px;
  width: 22px;
  height: 22px;
  border-radius: 50%;
  background: var(--v-primary-base, #43A047);
  display: flex;
  align-items: center;
  justify-content: center;
}

.package-icon-wrap {
  width: 56px;
  height: 56px;
  border-radius: 14px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 4px auto 14px;
}

.package-storage-amount {
  font-size: 19px;
  font-weight: 700;
  color: #0b0b0b;
  line-height: 1.2;
}

.package-description {
  font-size: 12px;
  color: #898781;
  margin-top: 3px;
  min-height: 16px;
}

.package-price {
  display: flex;
  align-items: baseline;
  justify-content: center;
  gap: 3px;
  margin-top: 14px;
}

.price-value {
  font-size: 22px;
  font-weight: 700;
  color: var(--v-primary-base, #43A047);
}

.price-period {
  font-size: 12px;
  color: #898781;
}

.package-divider {
  margin: 16px 0 14px;
}

.package-features {
  list-style: none;
  padding: 0;
  margin: 0;
  text-align: left;
}

.package-features li {
  display: flex;
  align-items: center;
  font-size: 12px;
  color: #52514e;
  padding: 4px 0;
}

.packages-actions {
  padding: 14px 24px !important;
  flex-wrap: wrap;
  gap: 8px;
}

.selection-summary {
  display: flex;
  align-items: center;
  font-size: 13px;
  color: #52514e;
}

.selection-summary--empty {
  color: #898781;
  font-style: italic;
}

.selection-summary-label {
  margin-right: 0;
  font-size: 12px;
  color: #898781;
}

.selection-summary-value {
  font-weight: 700;
  color: #0b0b0b;
  margin: 2px 0 4px 0;
  font-size: 14px;
}

.selection-summary-price {
  font-size: 13px;
  color: var(--v-primary-base, #43A047);
  font-weight: 600;
}

/* Storage and Duration Selection */
.storage-selection-section,
.duration-selection-section {
  margin-bottom: 24px;
}

.selection-title {
  font-size: 14px;
  font-weight: 600;
  color: #0b0b0b;
  margin: 0 0 12px 0;
  display: flex;
  align-items: center;
  gap: 6px;
}

.duration-grid {
  margin: -8px;
  width: calc(100% + 16px);
}

.duration-col {
  padding: 8px !important;
}

.duration-card {
  cursor: pointer;
  padding: 12px 16px;
  border-radius: 10px;
  border: 2px solid #e1e0d9;
  background: #ffffff;
  text-align: center;
  transition: all 0.2s ease;
  position: relative;
}

.duration-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
}

.duration-card--selected {
  border-color: var(--v-primary-base, #43A047);
  background-color: rgba(67, 160, 71, 0.06);
  box-shadow: 0 4px 12px rgba(67, 160, 71, 0.12);
}

.duration-label {
  font-size: 13px;
  font-weight: 600;
  color: #0b0b0b;
  line-height: 1.2;
}

.duration-discount {
  font-size: 11px;
  color: #0ca30c;
  font-weight: 700;
  margin-top: 3px;
  text-transform: uppercase;
}

/* Price Breakdown */
.price-breakdown {
  background: #f9f9f7;
  border-radius: 12px;
  padding: 16px;
  margin-top: 24px;
}

.breakdown-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-size: 13px;
  padding: 8px 0;
}

.breakdown-label {
  color: #898781;
  font-weight: 500;
}

.breakdown-value {
  color: #0b0b0b;
  font-weight: 600;
  font-variant-numeric: tabular-nums;
}

.breakdown-divider {
  margin: 12px 0 !important;
}

.breakdown-discount .breakdown-label {
  color: #0ca30c;
}

.breakdown-discount .breakdown-value {
  color: #0ca30c;
}

.breakdown-total {
  padding-top: 12px !important;
  border-top: 2px solid #e1e0d9;
  font-size: 15px;
}

.breakdown-total .breakdown-label {
  color: #0b0b0b;
  font-weight: 600;
}

.breakdown-total .breakdown-value {
  font-size: 18px;
  color: var(--v-primary-base, #43A047);
  font-weight: 700;
}

@media (max-width: 600px) {
  .storage-body {
    flex-direction: column;
  }

  .body-divider {
    width: 100%;
    height: 1px;
  }

  .stats-grid {
    width: 100%;
  }

  .footer-item {
    padding: 0 10px;
  }
}
</style>
